import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, Mail, ShieldCheck, ArrowLeft } from "lucide-react";
import { Logo } from "../components/site/Logo";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dest = location.state?.from || "/crm";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate(dest, { replace: true }); }, [user, dest, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate(dest, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) setError("Troppi tentativi falliti. Riprova tra qualche minuto.");
      else if (status === 401) setError("Email o password non corretti.");
      else setError("Errore di connessione. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy relative flex items-center justify-center px-4 overflow-hidden">
      <div className="noise-overlay" />
      <div className="pointer-events-none absolute -top-40 -left-40 h-[500px] w-[500px] rounded-full bg-brand-orange/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 h-[500px] w-[500px] rounded-full bg-white/5 blur-3xl" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md"
      >
        <div className="flex justify-center mb-6">
          <Logo markColor="#fff" textColor="#fff" />
        </div>

        <div className="rounded-3xl bg-white p-7 sm:p-8 shadow-2xl">
          <div className="flex items-center gap-2 text-navy">
            <ShieldCheck size={20} className="text-brand-orange" />
            <h1 className="font-display font-extrabold text-2xl">Area riservata</h1>
          </div>
          <p className="mt-2 text-sm text-ink/55">Accedi per gestire il CRM e la dashboard.</p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <label className="block text-sm font-semibold text-ink mb-1.5">Email</label>
              <div className="relative">
                <Mail size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink/40" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                  className="w-full rounded-xl border border-gray-200 pl-10 pr-4 py-3 text-[15px] outline-none focus:border-navy focus:ring-2 focus:ring-navy/10"
                  placeholder="nome@email.it"
                  autoComplete="username"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-ink mb-1.5">Password</label>
              <div className="relative">
                <Lock size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink/40" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  className="w-full rounded-xl border border-gray-200 pl-10 pr-4 py-3 text-[15px] outline-none focus:border-navy focus:ring-2 focus:ring-navy/10"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                />
              </div>
            </div>

            {error && (
              <p data-testid="login-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-3.5 text-base font-semibold text-white shadow-[0_10px_28px_-8px_rgba(242,140,40,0.8)] hover:bg-brand-orange-dark transition-colors disabled:opacity-70"
            >
              {loading ? <><Loader2 size={18} className="animate-spin" /> Accesso...</> : "Accedi"}
            </button>
          </form>
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white">
            <ArrowLeft size={15} /> Torna al sito
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
